import { useEffect, useState, useCallback } from "react";
import { ListChecks, CheckCircle2, AlertTriangle, MinusCircle, Circle, Loader2, ChevronDown, Settings2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/hooks/usePermissions";

const STATUS = {
  pending: { label: "Pending", cls: "text-muted-foreground", Icon: Circle },
  complete: { label: "Complete", cls: "text-success", Icon: CheckCircle2 },
  needs_attention: { label: "Needs attention", cls: "text-warning", Icon: AlertTriangle },
  not_applicable: { label: "Not applicable", cls: "text-muted-foreground", Icon: MinusCircle },
};

// Topic Readiness — reads the reusable checklist engine. Renders nothing when no
// active template applies (preserves prior behaviour). Per-item mutation uses the
// stable ChecklistItemInstance id and a PER-ITEM busy flag (interaction safety).
export function TopicReadiness({ topicId, statusKey, version, refreshKey, onChanged }) {
  const perms = usePermissions();
  const canManage = perms.has("view_checklist_templates");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [busyItem, setBusyItem] = useState(null);
  const [open, setOpen] = useState(true);
  const [naTarget, setNaTarget] = useState(null);
  const [naReason, setNaReason] = useState("");

  const load = useCallback(() => {
    apiFetch(`/checklists/readiness?subject_type=master_topic&subject_id=${topicId}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [topicId]);
  // reload when the topic version/status changes (e.g. after submit freeze / revision)
  useEffect(() => { setLoading(true); load(); }, [load, statusKey, version, refreshKey]);

  const start = async () => {
    setStarting(true);
    try { const d = await apiFetch("/checklists/start", { method: "POST", body: JSON.stringify({ subject_type: "master_topic", subject_id: topicId }) });
      setData(d); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setStarting(false); }
  };
  const setStatus = async (item, status, reason) => {
    setBusyItem(item.id);
    try {
      const d = await apiFetch(`/checklists/items/${item.id}`, { method: "PATCH", body: JSON.stringify({ status, not_applicable_reason: reason }) });
      setData(d); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusyItem(null); setNaTarget(null); setNaReason(""); }
  };

  if (loading) return <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-accent" /> Loading readiness…</div>;
  if (!data || !data.has_templates) return null;  // no active gate template → show nothing

  const s = data.summary;
  const frozen = data.instances.some((i) => i.frozen);
  const editable = data.can_edit && !frozen;

  return (
    <div data-testid="topic-readiness">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground"><ListChecks className="h-4 w-4" /> Topic Readiness</h2>
        {data.started && (
          <span className="text-[12px] text-muted-foreground" data-testid="readiness-summary">
            {s.complete} of {s.total} complete{s.blocking_count > 0 ? ` · ${s.blocking_count} required ${s.blocking_count === 1 ? "item needs" : "items need"} attention` : " · ready"}
          </span>
        )}
      </div>

      {frozen && <p className="mb-3 flex items-center gap-1.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground" data-testid="readiness-frozen"><Lock className="h-3.5 w-3.5" /> Submitted for review — this readiness record is now read-only.</p>}

      {!data.started ? (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-4" data-testid="readiness-not-started">
          <p className="text-sm text-muted-foreground">A readiness checklist applies to this Topic. {data.can_edit ? "Start it to track what's needed before review." : "It hasn't been started yet."}</p>
          {data.can_edit && <Button size="sm" className="mt-3 gap-1.5" onClick={start} disabled={starting} data-testid="readiness-start">{starting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start Checklist"}</Button>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="readiness-items">
          {data.instances.map((inst) => inst.items.map((it) => {
            const meta = STATUS[it.status] || STATUS.pending; const I = meta.Icon;
            const blocking = it.requirement_level === "mandatory" && (it.status === "pending" || it.status === "needs_attention");
            return (
              <div key={it.id} data-testid={`readiness-item-${it.id}`}
                className={cn("rounded-xl border p-3", blocking ? "border-warning/40 bg-warning/5" : "border-border/70")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <I className={cn("h-4 w-4 shrink-0", meta.cls)} />
                      <span className="break-words text-sm font-medium text-foreground">{it.title}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 pl-6 text-[11px] text-muted-foreground">
                      <span className={cn(it.requirement_level === "mandatory" ? "text-foreground/70" : "")}>{it.requirement_level === "mandatory" ? "Mandatory" : "Recommended"}</span>
                      {it.applicability_mode === "conditional" && <span>· Conditional</span>}
                      <span className={cn("font-medium", meta.cls)}>· {meta.label}</span>
                    </div>
                    {it.description && <p className="mt-1 break-words pl-6 text-[12px] text-muted-foreground">{it.description}</p>}
                    {it.applicability_mode === "conditional" && it.conditional_guidance && <p className="mt-1 pl-6 text-[12px] italic text-muted-foreground/80">{it.conditional_guidance}</p>}
                    {it.status === "not_applicable" && it.not_applicable_reason && <p className="mt-1 pl-6 text-[12px] text-muted-foreground">N/A: {it.not_applicable_reason}</p>}
                    <p className="mt-1 pl-6 text-[11px] text-muted-foreground/80" data-testid={`readiness-authority-${it.id}`}>
                      {it.completion_authority_mode === "permission" ? `Requires: ${it.completion_permission_label} permission` : "Can be completed by: Subject editors"}
                    </p>
                    {!frozen && !it.can_complete && <p className="mt-0.5 pl-6 text-[11px] text-muted-foreground">An authorised reviewer needs to complete this item.</p>}
                  </div>
                  {!frozen && it.can_complete && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="shrink-0 gap-1" disabled={busyItem === it.id} data-testid={`readiness-set-${it.id}`}>
                          {busyItem === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Set <ChevronDown className="h-3.5 w-3.5" /></>}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => setStatus(it, "complete")} data-testid={`readiness-complete-${it.id}`}><CheckCircle2 className="mr-2 h-4 w-4 text-success" /> Complete</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(it, "needs_attention")}><AlertTriangle className="mr-2 h-4 w-4 text-warning" /> Needs attention</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(it, "pending")}><Circle className="mr-2 h-4 w-4" /> Pending</DropdownMenuItem>
                        {it.applicability_mode === "conditional" && <DropdownMenuItem onClick={() => { setNaTarget(it); setNaReason(""); }} data-testid={`readiness-na-${it.id}`}><MinusCircle className="mr-2 h-4 w-4" /> Not applicable…</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          }))}
        </div>
      )}

      <div className="mt-3">
        {canManage
          ? <Link to="/settings/checklists" className="text-[12px] font-medium text-accent hover:underline" data-testid="readiness-manage">Manage Checklists</Link>
          : null}
      </div>

      <Dialog open={!!naTarget} onOpenChange={(o) => !o && setNaTarget(null)}>
        <DialogContent data-testid="readiness-na-dialog">
          <DialogHeader><DialogTitle>Mark “{naTarget?.title}” Not Applicable</DialogTitle></DialogHeader>
          <div className="space-y-2 py-1">
            <Label className="text-xs">Reason <span className="text-destructive">*</span></Label>
            <Textarea value={naReason} onChange={(e) => setNaReason(e.target.value)} rows={3} placeholder="Why doesn't this apply to this Topic?" autoFocus data-testid="readiness-na-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNaTarget(null)}>Cancel</Button>
            <Button onClick={() => setStatus(naTarget, "not_applicable", naReason)} disabled={!naReason.trim() || busyItem === naTarget?.id} data-testid="readiness-na-confirm">Mark Not Applicable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
